import {
  clearTokens,
  getRefreshToken,
  getRememberPreference,
  getToken,
  setTokens,
  setTermsAccepted
} from './auth';

declare const __API_URL__: string;
export const baseUrl: string = typeof __API_URL__ !== 'undefined' ? __API_URL__ : 'http://localhost:4000';

export class ApiError extends Error {
  readonly status: number;
  readonly body: Record<string, unknown>;

  constructor(message: string, status: number, body: Record<string, unknown>) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

/**
 * Detect a structured backend error code (e.g. PII_MIXED_FINDINGS_RESOLVE_TEXT_FIRST).
 * Returns the code string when present so callers can branch on UX-friendly handling
 * without leaking ApiError internals across the codebase.
 */
export function getApiErrorCode(err: unknown): string | null {
  if (err instanceof ApiError) {
    const code = err.body?.code;
    if (typeof code === 'string') return code;
  }
  return null;
}

interface RequestOptions {
  auth?: boolean;
  retryOnAuthError?: boolean;
  /** Internal flag to disable step-up retry on the second attempt. */
  retryOnStepUp?: boolean;
  /** Sprint 3.4 (FE 7.51.0): extra headers merged with auth headers.
   *  Use case: X-Device-Fingerprint in selectOrg call. */
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

/** Sprint 3.4 (FE 7.51.0): event names exchanged between requestJson and the
 *  global StepUpModal mount. Caller of api.ts methods receives transparent
 *  retry — no manual try/catch needed. */
export const STEP_UP_EVENT_REQUIRED = 'dominoris.step-up-required';
export const STEP_UP_EVENT_VERIFIED = 'dominoris.step-up-verified';
export const STEP_UP_EVENT_CANCELLED = 'dominoris.step-up-cancelled';

/** Wait for a window event ('verified' or 'cancelled') with timeout fallback. */
async function waitForStepUpVerification(timeoutMs = 60_000): Promise<boolean> {
  if (typeof window === 'undefined') return false;
  return new Promise<boolean>((resolve) => {
    const cleanup = (): void => {
      window.removeEventListener(STEP_UP_EVENT_VERIFIED, onVerified);
      window.removeEventListener(STEP_UP_EVENT_CANCELLED, onCancelled);
      clearTimeout(timer);
    };
    const onVerified = (): void => { cleanup(); resolve(true); };
    const onCancelled = (): void => { cleanup(); resolve(false); };
    const timer = setTimeout(() => { cleanup(); resolve(false); }, timeoutMs);
    window.addEventListener(STEP_UP_EVENT_VERIFIED, onVerified, { once: true });
    window.addEventListener(STEP_UP_EVENT_CANCELLED, onCancelled, { once: true });
  });
}

let refreshPromise: Promise<string | null> | null = null;
let refreshTimer: ReturnType<typeof setTimeout> | null = null;

/** Schedule a proactive token refresh 60s before expiry. */
export function scheduleProactiveRefresh(expiresAt: string): void {
  if (refreshTimer) clearTimeout(refreshTimer);
  const msUntilExpiry = new Date(expiresAt).getTime() - Date.now();
  const delay = Math.max(msUntilExpiry - 60_000, 5_000);
  refreshTimer = setTimeout(() => {
    refreshAccessToken().catch(() => {
      // Proactive refresh failed — will retry reactively on next 401
    });
  }, delay);
}

function authHeaders(): Record<string, string> {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Attempts to refresh the access token.
 * Returns new access token on success, null if session is truly dead
 * (no refresh token or refresh token rejected by server).
 * Throws on transient errors (network, 429, 500) to avoid killing the session.
 */
export async function refreshAccessToken(): Promise<string | null> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) return null;

    const res = await fetch(`${baseUrl}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ refreshToken })
    });

    if (res.status === 401 || res.status === 403) {
      // Refresh token invalid/expired — session over
      return null;
    }

    if (!res.ok) {
      // Transient error (429, 500) — throw to prevent session kill
      throw new Error(`Token refresh failed: ${res.status}`);
    }

    const data = (await res.json()) as {
      accessToken: string;
      refreshToken: string;
      expiresAt: string;
      termsAccepted?: boolean;
    };
    setTokens(data.accessToken, data.refreshToken, getRememberPreference());
    setTermsAccepted(data.termsAccepted ?? false);
    scheduleProactiveRefresh(data.expiresAt);
    return data.accessToken;
  })().finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
}

async function requestJson<T>(
  path: string,
  init: RequestInit,
  options: RequestOptions = {}
): Promise<T> {
  const useAuth = options.auth !== false;
  const canRetry = options.retryOnAuthError !== false;
  const headers = {
    ...(useAuth ? authHeaders() : {}),
    ...(init.headers ?? {}),
    ...(options.headers ?? {})
  } as Record<string, string>;

  const res = await fetch(`${baseUrl}${path}`, {
    ...init,
    headers,
    credentials: 'include',
    signal: options.signal
  });

  if (res.status === 401 && useAuth && canRetry) {
    try {
      const newToken = await refreshAccessToken();
      if (newToken) {
        return requestJson<T>(path, init, { ...options, retryOnAuthError: false });
      }
      // null = refresh token truly invalid — kill session
      clearTokens();
      throw new Error('Unauthorized');
    } catch (err) {
      // Transient refresh error — don't clear tokens, let the request fail
      if (err instanceof Error && err.message === 'Unauthorized') throw err;
      throw new ApiError('Authentication temporarily unavailable', 401, {});
    }
  }

  if (res.status === 401) {
    let message = 'Unauthorized';
    let body: Record<string, unknown> = {};
    try {
      body = (await res.json()) as Record<string, unknown>;
      if (body.message) message = String(body.message);
    } catch { /* no JSON body */ }

    // Sprint 3.4 (FE 7.51.0): backend StepUpGuard responde 401 STEP_UP_REQUIRED
    // pra paths AAL3 (PII original, admin destructive). Em vez de matar a session,
    // dispatch event pro StepUpModal global → user verifica passkey → retry once.
    const isStepUp = message === 'STEP_UP_REQUIRED' || body.code === 'STEP_UP_REQUIRED';
    const canRetryStepUp = options.retryOnStepUp !== false;
    if (isStepUp && canRetryStepUp && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(STEP_UP_EVENT_REQUIRED));
      const verified = await waitForStepUpVerification();
      if (verified) {
        return requestJson<T>(path, init, { ...options, retryOnStepUp: false });
      }
      throw new ApiError('Step-up cancelled', 401, body);
    }

    if (useAuth) clearTokens();
    throw new Error(message);
  }

  if (!res.ok) {
    let message = `Request failed: ${res.status}`;
    let body: Record<string, unknown> = {};
    try {
      body = (await res.json()) as Record<string, unknown>;
      if (body.message) {
        message = Array.isArray(body.message)
          ? (body.message as string[]).join(', ')
          : String(body.message);
      }
    } catch {
      // response body not JSON, keep generic message
    }
    throw new ApiError(message, res.status, body);
  }

  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

export async function getJson<T>(path: string, options?: RequestOptions): Promise<T> {
  return requestJson<T>(path, { method: 'GET' }, options);
}

export async function postJson<T, B>(
  path: string,
  body: B,
  options?: RequestOptions
): Promise<T> {
  return requestJson<T>(
    path,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    },
    options
  );
}

export async function postForm<T>(
  path: string,
  body: FormData,
  options?: RequestOptions
): Promise<T> {
  return requestJson<T>(
    path,
    {
      method: 'POST',
      body
    },
    options
  );
}

export async function putJson<T, B>(
  path: string,
  body: B,
  options?: RequestOptions
): Promise<T> {
  return requestJson<T>(
    path,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    },
    options
  );
}

export async function patchJson<T, B>(
  path: string,
  body: B,
  options?: RequestOptions
): Promise<T> {
  return requestJson<T>(
    path,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    },
    options
  );
}

export async function deleteJson<T>(path: string, options?: RequestOptions): Promise<T> {
  return requestJson<T>(path, { method: 'DELETE' }, options);
}

export async function logout(): Promise<void> {
  if (refreshTimer) clearTimeout(refreshTimer);
  try {
    await postJson('/auth/logout', {});
  } catch {
    // Server logout is best-effort; always clear local tokens
  } finally {
    clearTokens();
  }
}

/**
 * Internal core: fetch a binary resource and return the raw Blob, applying
 * the same refresh-on-401 and MIME allowlist as fetchBlob. Returns null on
 * any failure (network, auth dead, disallowed content-type).
 */
async function fetchBlobCore(path: string): Promise<Blob | null> {
  try {
    let res = await fetch(`${baseUrl}${path}`, { headers: authHeaders(), credentials: 'include' });
    if (res.status === 401) {
      try {
        const newToken = await refreshAccessToken();
        if (newToken) {
          res = await fetch(`${baseUrl}${path}`, { headers: authHeaders(), credentials: 'include' });
        } else {
          clearTokens();
          return null;
        }
      } catch {
        // Transient refresh error — return null without killing session
        return null;
      }
    }
    if (!res.ok) return null;
    const blob = await res.blob();
    // Cycle 11 v7.37.0: MIME validation defesa-em-profundidade. Se servidor
    // retorna text/html (ex: erro proxy 200 com body HTML), <object data>
    // renderizaria HTML no contexto do app — XSS via blob URL. Aceitar
    // apenas image/* + application/pdf (tipos servidos por endpoints
    // /pii/review-image e /evolution-notes/file). text/plain liberado pra
    // exports legitimos quando aplicavel.
    const contentType = blob.type.toLowerCase();
    // EXK-50: application/octet-stream liberado porque BE agora retorna esse
    // fallback honesto pra extensoes desconhecidas (era 'image/jpeg' mentiroso
    // antes). Risco XSS via <object data> mitigado downstream: PiiResolveModal
    // valida via magic bytes (isPdfBlob) e so renderiza canvas/img/pdf reais.
    const allowed =
      contentType.startsWith('image/') ||
      contentType === 'application/pdf' ||
      contentType === 'application/octet-stream' ||
      contentType.startsWith('text/plain') ||
      contentType === '';
    if (!allowed) {
      console.warn('fetchBlob rejected unexpected content-type:', contentType);
      return null;
    }
    return blob;
  } catch (err: unknown) {
    console.warn('fetchBlob failed:', err instanceof Error ? err.message : err);
    return null;
  }
}

export async function fetchBlob(path: string): Promise<string | null> {
  const blob = await fetchBlobCore(path);
  return blob ? URL.createObjectURL(blob) : null;
}

/**
 * Variant of fetchBlob that returns the raw Blob instead of a blob URL.
 * Caller is responsible for `URL.createObjectURL` + `URL.revokeObjectURL`.
 * Used when the caller needs to inspect bytes (e.g. magic-byte detection
 * for PDF→PNG conversion in PiiResolveModal).
 */
export async function fetchBlobObject(path: string): Promise<Blob | null> {
  return fetchBlobCore(path);
}

export async function acceptTermsRequest(documentVersionIds: number[]): Promise<void> {
  // Cycle 9 v7.35.0: removida dupla persistencia. AuthContext.acceptTerms()
  // ja chama setTermsAcceptedState + setTermsAccepted via storage. Manter
  // ambos cria janela onde state React/storage divergem (gate pode
  // re-aparecer momentaneamente em refresh).
  await postJson('/auth/accept-terms', { documentVersionIds });
}

/** Check if an error is an HTTP error with a specific status code. */
export function isHttpError(err: unknown, status: number): boolean {
  if (err && typeof err === 'object' && 'status' in err) {
    return (err as { status: number }).status === status;
  }
  return false;
}

export async function googleLogin(idToken: string): Promise<{
  preAuthToken: string;
  organizations: import('./types').OrgOption[];
  /** Sprint 2 (backend dc13a7f): when true, FE must call passkeyGoogleVerify
   *  with a WebAuthn challenge response BEFORE selectOrg. Backend gates this
   *  via PASSKEY_GATE_ENABLED env var; legacy clients can ignore the flag
   *  while gate is OFF (selectOrg accepts the token unchanged). */
  passkeyVerifyRequired?: boolean;
}> {
  return postJson('/auth/google', { idToken }, { auth: false, retryOnAuthError: false });
}

export async function validateInvite(token: string): Promise<{
  memberName: string | null;
  orgName: string;
  registrationRequired: boolean;
}> {
  return getJson(`/invite/${token}`, { auth: false });
}

export async function completeInvite(
  token: string,
  googleIdToken: string,
  registrationSuffix?: string
): Promise<{
  preAuthToken: string;
  organizations: Array<{ id: number; name: string; role: import('./types').Role }>;
}> {
  return postJson(
    `/invite/${token}/complete`,
    { googleIdToken, registrationSuffix },
    { auth: false, retryOnAuthError: false }
  );
}

export async function generateInviteBatch(
  memberIds: number[]
): Promise<Array<{ memberId: number; memberName: string; token: string; url: string }>> {
  return postJson('/invite/generate/batch', { memberIds });
}

export async function generateStaffInvite(
  name: string,
  role?: string
): Promise<{ url: string }> {
  return postJson('/invite/generate/staff', { name, role: role ?? 'collaborator' });
}

/**
 * EXK-153: each row now declares `kind` so the UI can render badges and decide
 * which actions apply. `first_access` rows come from `recovery_tokens` (admin
 * magic-links) and carry `email`; `invite` rows come from `invite_tokens`.
 */
export interface InviteListItem {
  kind: 'invite' | 'first_access';
  id: number;
  memberId: number | null;
  name: string | null;
  email: string | null;
  role: string;
  expiresAt: string;
  usedAt: string | null;
  revokedAt: string | null;
  createdAt: string;
  status: string;
  token: string | null;
  // EXK-157 Fase B: status reportado pelo webhook Resend.
  // null = nunca rastreado (token pré-Fase B ou envio sem Resend).
  // Valores: sent, delivered, delayed, bounced, complained, failed.
  emailStatus: string | null;
  emailStatusAt: string | null;
}

export async function listInvites(): Promise<InviteListItem[]> {
  return getJson('/invite/manage/list');
}

export async function revokeInvite(id: number): Promise<void> {
  await deleteJson(`/invite/manage/${id}`);
}

export async function regenerateInvite(id: number): Promise<{ memberId: number; memberName: string; token: string; url: string }> {
  return postJson(`/invite/manage/${id}/regenerate`, {});
}

export async function passkeyRegisterOptions(): Promise<Record<string, unknown>> {
  return postJson('/auth/passkey/register/options', {});
}

export async function passkeyRegisterVerify(response: Record<string, unknown>): Promise<{ verified: boolean }> {
  return postJson('/auth/passkey/register/verify', { response: JSON.stringify(response) });
}

export async function passkeyAuthOptions(email?: string): Promise<Record<string, unknown>> {
  return postJson('/auth/passkey/auth/options', { email }, { auth: false, retryOnAuthError: false });
}

export async function passkeyAuthVerify(response: Record<string, unknown>): Promise<{
  preAuthToken: string;
  organizations: Array<{ id: number; name: string; role: string }>;
}> {
  return postJson('/auth/passkey/auth/verify', { response: JSON.stringify(response) }, { auth: false, retryOnAuthError: false });
}

/** Sprint 2 (backend dc13a7f): trade a Google-issued preAuthToken (with
 *  passkeyVerifyRequired=true) for a passkey-verified preAuthToken that
 *  selectOrg accepts. Anti-account-sharing: stolen Google account alone
 *  cannot reach the app — attacker also needs the passkey hardware. */
export async function passkeyGoogleVerify(
  preAuthToken: string,
  response: Record<string, unknown>
): Promise<{
  preAuthToken: string;
  organizations: import('./types').OrgOption[];
  passkeyVerifyRequired?: boolean;
}> {
  return postJson(
    '/auth/passkey/google-verify',
    { preAuthToken, response: JSON.stringify(response) },
    { auth: false, retryOnAuthError: false }
  );
}

/** Sprint 3 (backend a691bce): re-verify passkey to refresh
 *  UserSession.last_passkey_verify_at. Required by StepUpGuard for AAL3
 *  endpoints (currently GET /pii/:imageId/original). */
export async function passkeyStepUpVerify(
  response: Record<string, unknown>
): Promise<{ verified: true; verifiedAt: string }> {
  return postJson(
    '/auth/passkey/step-up/verify',
    { response: JSON.stringify(response) }
  );
}

// ─── Recovery flow (Sprint 3.1 backend / FE 7.51.0) ─────────────────────────

/** Magic-link recovery start: Google verify proves identity → email magic link. */
export async function recoveryStart(idToken: string): Promise<{ recoveryEmailSent: boolean }> {
  return postJson(
    '/auth/recovery/start',
    { idToken },
    { auth: false, retryOnAuthError: false }
  );
}

/** Validate magic-link token + return WebAuthn registration options. */
export async function recoveryOptions(token: string): Promise<Record<string, unknown>> {
  return postJson(
    '/auth/recovery/options',
    { token },
    { auth: false, retryOnAuthError: false }
  );
}

/** Complete recovery: enroll new passkey + revoke old credentials/sessions. */
export async function recoveryEnroll(
  token: string,
  response: Record<string, unknown>
): Promise<{ verified: true }> {
  return postJson(
    '/auth/recovery/enroll',
    { token, response: JSON.stringify(response) },
    { auth: false, retryOnAuthError: false }
  );
}
